import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'

import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'

import List from 'components/Common/List'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { StyledModalImageThumbnail } from 'components/Luxkit/Modal/ModalImageThumbnail'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Typography from 'components/Luxkit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { GooglePlaceRating } from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import RestoreCartModalFooter from 'tripPlanner/components/TripModal/TripItems/Common/RestoreCartModalFooter'
import AddressWithViewMapButton from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddressWithViewMapButton'
import ExperienceSummaryModalFooter from 'tripPlanner/components/TripModal/TripItems/Summary/Common/SummaryModalFooter/ExperienceSummaryModalFooter'
import ReadOnlyItemFooter from 'tripPlanner/components/TripModal/TripItems/Summary/Common/SummaryModalFooter/ReadOnlyItemFooter'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience, useOrder } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { ExperienceItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isLeBookmark, maybePlaceId } from 'tripPlanner/utils'
import { isBookableItem } from 'tripPlanner/utils/bookmark/restoreCart'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

interface Props {
  item: ExperienceItem
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.six};
`

const BookingDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  padding-bottom: ${rem(20)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.six};
`

const Dates = styled.div`
  display: flex;
  justify-content: space-between;
`

const Tickets = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${rem(16)};
`

const Date = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const Duration = Date
const ImageElement = styled.img`
  overflow: hidden;
  object-fit: cover;
  object-position: center;
  width: 100%;
  height: 168px;

  ${mediaQueryUp.tablet} {
    height: 315px;
  }
`

function ExperienceItemSummary(props: Props) {
  const { item } = props
  const { startDate, startTime } = props.item
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = tripContext?.viewAsTemplate ?? false
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const isBookable = isLeBookmark(item) && isBookableItem(item)

  const orderId = getOrderId(item)

  const { data: order, isLoading: isOrderLoading } = useOrder(orderId)
  const experienceId = item.code
  const { data: experience, isLoading: isExperienceLoading } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const isLoading = (!!orderId && isOrderLoading) || isExperienceLoading

  const { closeModal, goBack } = useModalCallbacks()

  const getOfferUrl = useCallback(() => {
    if (experience) {
      return getExperienceOfferPageURL(experience)
    }
  }, [experience])
  const offerUrl = getOfferUrl()

  const durationString = useMemo(() => {
    if (experience) {
      return getExperienceDurationString(experience)
    }
  }, [experience])
  const hasDuration = useMemo(
    () =>
      !!(experience && (experience.duration.from || experience.duration.to)),
    [experience],
  )
  const placeId = maybePlaceId(item.place)

  return (
    <>
      <ModalHeader
        title={item.name}
        subtitle={item.place.name}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        {experience?.images?.[0].url && (
          <StyledModalImageThumbnail>
            <ImageElement
              src={experience.images[0].url}
              alt={experience.images[0]?.title}
            />
          </StyledModalImageThumbnail>
        )}
        {!experience && item.place?.type === 'GEO' && (
          <StyledModalImageThumbnail>
            <ImageElement src={item.place.photoUrl} alt={item.place.name} />
          </StyledModalImageThumbnail>
        )}
        <ModalContent>
          {isLoading && <LoadingIndicator inline />}
          {!isLoading && (
            <VerticalSpacer gap={20}>
              <ItemTypeAndBookedLabel item={item} />
              <Header>
                {experience && (
                  <Typography align="start">
                    {experience.copy.highlights.length === 0 && (
                      <MarkdownRender
                        fontSize="large"
                        content={experience.copy.description}
                      />
                    )}
                    {experience.copy.highlights.length > 0 && (
                      <List>
                        {experience.copy.highlights.map((highlight) => (
                          <li key={highlight}>
                            <BodyText variant="large" as="span">
                              {highlight}
                            </BodyText>
                          </li>
                        ))}
                      </List>
                    )}
                  </Typography>
                )}
                {offerUrl && (
                  <TextLink
                    weight="bold"
                    to={offerUrl}
                    onClick={() =>
                      setImmersiveMode({
                        tripId: item.tripId,
                        sectionId: itemSectionId(item.id),
                      })
                    }
                  >
                    View all details
                  </TextLink>
                )}
              </Header>
              {order && (
                <BookingDetails>
                  <Heading variant="heading5">Your Booking Details</Heading>
                  <div>
                    <BodyText variant="medium" weight="semi-bold">
                      Total Paid
                    </BodyText>
                    <BodyText variant="large">${order.total}</BodyText>
                  </div>
                  <Tickets>
                    {order.experienceItems.map((experienceItem, index) => (
                      <div key={experienceItem.id}>
                        <BodyText variant="medium" weight="semi-bold">
                          Ticket {index + 1}
                        </BodyText>
                        <BodyText variant="large">
                          {experienceItem.ticket.name}
                        </BodyText>
                        <BodyText variant="large" colour="neutral-two">
                          {experienceItem.bookingNumber}
                        </BodyText>
                      </div>
                    ))}
                  </Tickets>
                </BookingDetails>
              )}
              <Dates>
                <Date>
                  <BodyText variant="medium" weight="semi-bold">
                    Date
                  </BodyText>
                  <BodyText variant="large">
                    {[
                      startDate?.format('ddd, DD MMMM YYYY'),
                      startTime?.format('h:mma'),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </BodyText>
                </Date>
                {hasDuration && (
                  <Duration>
                    <BodyText variant="medium" weight="semi-bold">
                      Duration
                    </BodyText>
                    <BodyText variant="large">{durationString}</BodyText>
                  </Duration>
                )}
              </Dates>
              {experience && (
                <AddressWithViewMapButton
                  placeId={placeId}
                  addressLabel="Pick up"
                  address={experience.location.name}
                />
              )}
              {placeId && (
                <GooglePlaceRating size="M" placeId={placeId} label="Reviews" />
              )}
            </VerticalSpacer>
          )}
        </ModalContent>
      </ModalBody>

      {!viewAsTemplate && canEdit && (
        <>
          {isBookable && <RestoreCartModalFooter item={item} />}
          {!isBookable && <ExperienceSummaryModalFooter item={item} />}
        </>
      )}
      {!viewAsTemplate && !canEdit && <ReadOnlyItemFooter item={item} />}
    </>
  )
}

export default ExperienceItemSummary
